import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ModulePane } from '../components/ModulePane/ModulePane';
import { AccessNotice } from '../components/AccessNotice/AccessNotice';
import { isAccessDenied } from '../lib/dataverse';
import { useCurrentUser, useComposeRole, useCan, useTeamDisplayName, ROLE_CAPS } from '../lib/identity';
import { fetchRecentCorrespondence, type Correspondence } from '../lib/compose';
import styles from './Home.module.css';

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const STATUS_LABEL: Record<string, string> = {
  Draft: 'Draft',
  CopiedToActuris: 'Copied to Acturis',
  Sent: 'Sent',
  WrittenToActuris: 'Written to Acturis',
};

export function Home() {
  const user = useCurrentUser();
  const { role } = useComposeRole();
  const canReview = useCan('review');
  const canCompose = useCan('compose');
  const canManageUsers = useCan('manageUsers');
  const canEditLibrary = useCan('editLibrary');
  const displayNameFor = useTeamDisplayName();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['compose.correspondence'],
    queryFn: fetchRecentCorrespondence,
    enabled: !!user,
  });
  const all = q.data ?? [];

  const [search, setSearch] = useState('');
  const [mineOnly, setMineOnly] = useState(true);

  const rows = useMemo(() => {
    let r = all;
    // Non-reviewers only ever see their own drafts (review cap gates QA
    // access to others'). Reviewers/admins may toggle.
    if (mineOnly || !canReview) {
      r = r.filter((c) => (c.gaqc_createdbyupn ?? '').toLowerCase() === user?.upn);
    }
    const t = search.trim().toLowerCase();
    if (t) {
      r = r.filter(
        (c) =>
          (c.gaqc_clientname ?? '').toLowerCase().includes(t) ||
          (c.gaqc_correspondenceref ?? '').toLowerCase().includes(t) ||
          (c.gaqc_policyyearref ?? '').toLowerCase().includes(t)
      );
    }
    return r;
  }, [all, search, mineOnly, canReview, user?.upn]);

  const sidebar = (
    <>
      <div className={styles.navTop}>
        <div className={styles.navTitle}>SODAN drafts</div>
        <div className={styles.navSub}>{q.data ? `${rows.length} shown` : '…'}</div>
      </div>

      {canCompose && (
        <Link to="/new" className={styles.newBtn}>
          + New SODAN
        </Link>
      )}

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="h-search">Search</label>
        <input
          id="h-search"
          type="search"
          className={styles.search}
          placeholder="Client, ref, policy year…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {canReview && (
        <label className={styles.checkRow}>
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(e) => setMineOnly(e.target.checked)}
          />
          My drafts only
        </label>
      )}

      <div className={styles.roleNote}>
        Signed in as <strong>{role}</strong>
        <span className={styles.capList}>
          {(Object.keys(ROLE_CAPS[role]) as Array<keyof typeof ROLE_CAPS['user']>)
            .filter((c) => ROLE_CAPS[role][c])
            .join(' · ') || 'no capabilities'}
        </span>
      </div>

      {(canEditLibrary || canManageUsers) && (
        <Link to="/admin" className={styles.adminLink}>
          ⚙ Admin
        </Link>
      )}
    </>
  );

  return (
    <ModulePane sidebar={sidebar} ariaLabel="Compose navigation">
      {q.isLoading && <div className={styles.state}>Loading drafts…</div>}
      {q.isError &&
        (isAccessDenied(q.error) ? (
          <AccessNotice />
        ) : (
          <div className={styles.state}>
            Couldn’t load drafts.{' '}
            <button type="button" className={styles.retry} onClick={() => q.refetch()}>
              Retry
            </button>
          </div>
        ))}
      {q.isSuccess && (
        <div className={styles.results}>
          <header className={styles.resultsHeader}>
            <div>
              <h1 className={styles.resultsTitle}>
                {mineOnly || !canReview ? 'My SODAN drafts' : 'All SODAN drafts'}{' '}
                <span className={styles.resultsCount}>· {rows.length}</span>
              </h1>
              <p className={styles.resultsLead}>
                Demands &amp; needs statements generated in Compose. Click one to view, copy, or mark as copied to Acturis.
              </p>
            </div>
            {canCompose && (
              <button type="button" className={styles.primaryBtn} onClick={() => navigate('/new')}>
                + New SODAN
              </button>
            )}
          </header>

          {rows.length === 0 ? (
            <div className={styles.empty}>
              {all.length === 0
                ? canCompose
                  ? 'No SODAN drafts yet. Click + New SODAN to create the first one.'
                  : 'No SODAN drafts yet.'
                : 'No drafts match your search.'}
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Ref</th>
                    <th>Client</th>
                    <th>Policy year</th>
                    <th>Status</th>
                    <th>Created by</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c: Correspondence) => (
                    <tr
                      key={c.gaqc_correspondenceref}
                      className={styles.row}
                      onClick={() => navigate(`/c/${encodeURIComponent(c.gaqc_correspondenceref)}`)}
                    >
                      <td className={styles.ref}>{c.gaqc_correspondenceref}</td>
                      <td>{c.gaqc_clientname || '—'}</td>
                      <td className={styles.mono}>{c.gaqc_policyyearref || '—'}</td>
                      <td>
                        <span className={`${styles.statusChip} ${styles[`status_${c.gaqc_status}`] ?? ''}`}>
                          {STATUS_LABEL[c.gaqc_status ?? ''] ?? c.gaqc_status ?? '—'}
                        </span>
                      </td>
                      <td className={styles.byCell} title={c.gaqc_createdbyupn || undefined}>
                        {c.gaqc_createdbyupn ? displayNameFor(c.gaqc_createdbyupn) : '—'}
                      </td>
                      <td className={styles.dateCell}>{fmtDate(c.gaqc_createdatutc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ModulePane>
  );
}
